import React, { useState, useEffect } from 'react';
import MainPageLayout from '../../components/common/MainPageLayout';
import FormCard from '../../components/common/FormCard';
import { AddLegalPageIcon, AddTemplateIcon } from '../../components/common/SvgIcon';
import Pagination from '../../components/common/Pagination';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import TemplateSearchBar from './components/Templatesearchbar';
import TemplateTabs from './components/Templatetabs';
import TemplatesTable from './components/Templatestable';
import { Toast } from '../../components/common/Toast';

const LegalPageTemplates = ({ page }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    all: 0,
    free: 0,
    pro: 0,
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    templateId: null,
    templateName: '',
  });

  const isProActive = LEGAL_PAGES?.license?.status === 'valid';
  const [perPage, setPerPage] = useState(20);

  // Fetch templates from API with pagination
  const fetchTemplates = async (currentPage, type, search) => {
    setLoading(true);
    try {
      let url = `${LEGAL_PAGES.apiUrl}/templates?page=${currentPage}&per_page=${perPage}`;

      if (type !== 'all') {
        url += `&type=${type}`;
      }

      if (search.trim()) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTemplates(result.data.templates || []);
        setTotal(result.data.total || 0);
        setTotalPages(result.data.total_pages || 1);

        if (result.data.counts) {
          setCounts(result.data.counts);
        }
      } else {
        Toast.error('Failed to load templates');
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      Toast.error('An error occurred while loading templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchTemplates(page, activeTab, searchQuery);
  }, [activeTab, page, searchQuery]);

  // Manual navigation function using hash
  const navigate = (path) => {
    window.location.hash = path;
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    window.location.hash = '/legal-page-templates';
  };

  const handleSearch = (query) => {
    if (query === searchQuery) return;
    setSearchQuery(query);
    window.location.hash = '/legal-page-templates';
  };

  const handleEditClick = (template) => {
    if (template.type === 'pro' && !isProActive) {
      Toast.warning('Please upgrade to PRO to edit this template');
      return;
    }
    // Navigate to edit page
    window.location.hash = `/edit-template/${template.id}`;
  };

  // Handle delete click
  const handleDeleteClick = (templateId, templateName) => {
    setDeleteModal({
      isOpen: true,
      templateId,
      templateName,
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${LEGAL_PAGES.apiUrl}/template/${deleteModal.templateId}`,
        {
          method: 'DELETE',
          headers: {
            'X-WP-Nonce': LEGAL_PAGES.nonce,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        Toast.success('Template deleted successfully!');
        setDeleteModal({ isOpen: false, templateId: null, templateName: '' });
        fetchTemplates(page, activeTab, searchQuery); // Refresh the list with current filters
      } else {
        Toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      Toast.error('An error occurred while deleting');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, templateId: null, templateName: '' });
  };

  const handleCreateClick = () => {
    adl_legal_pages__modal(true);
    navigate('/add-new-template');
    setTimeout(() => {
      adl_legal_pages__modal(false);
    }, 500);
  };

  return (
    <MainPageLayout
      title="Legal Page Templates"
      description="Manage and customize your legal page templates"
      button={
        <button
          className="adl-add-button"
          onClick={handleCreateClick}
        >
          <AddLegalPageIcon />
          Create from Template
        </button>
      }
    >
      <FormCard className="min-h-auto">
        <div className="flex items-center gap-4 justify-start">
          <TemplateSearchBar onSearch={handleSearch} />

          <TemplateTabs
            activeTab={activeTab}
            counts={counts}
            onTabChange={handleTabChange}
          />
        </div>

        <TemplatesTable
          templates={templates}
          loading={loading}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          isProActive={isProActive}
        />

        {!loading && totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              baseSlug="legal-page-templates"
              current={page}
              total={totalPages}
            />
          </div>
        )}
      </FormCard>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteModal.templateName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={false}
      />
    </MainPageLayout>
  );
};

export default LegalPageTemplates;